<template>
    <div class="optInSection">
        <div class="complianceModal__column">
            <div class="optInSection__item">
                <form-dropdown
                    ref="dropdownMethods"
                    v-model="method"
                    :label="optInLabel"
                    :placeholder="'e.g. via Text'"
                    :settings="dropdownSettings"
                    :hideLabelColon="true"
                    :hideRequired="true"
                    :tooltip="{
                        content:
                            'Select how your customers opt-in to receive text message. View the examples provided to ensure the screenshots you provide are accepted.',
                    }"
                    @input="handleChangeOptInMethods"
                />

                <a v-if="!isAI" class="optInSection__example" :href="EXAMPLE_LINK" target="_blank">
                    See example
                </a>
            </div>
        </div>

        <div v-if="isAI" class="complianceModal__column">
            <div class="optInSection__item images">
                <div class="images__box">
                    <div
                        class="preview"
                        :style="{
                            'background-image': `url(${currentOptInImage.approvedSourceUrl})`,
                        }"
                    >
                        <div class="preview__hover">
                            <div
                                class="icon-block"
                                @click="handleShowPreview(currentOptInImage.approvedSourceUrl)"
                            >
                                <vector-icon icon="eye" :size="18" :fill="'#7A7A7A'" />
                            </div>
                        </div>
                    </div>
                    <span class="images__icon">
                        <vector-icon icon="approved" :size="14" :fill="'#13B763'" />
                        <span class="images__label"> Approved </span>
                    </span>
                </div>
                <div class="images__box">
                    <div
                        class="preview"
                        :style="{
                            'background-image': `url(${currentOptInImage.rejectedSourceUrl})`,
                        }"
                    >
                        <div class="preview__hover">
                            <div
                                class="icon-block"
                                @click="handleShowPreview(currentOptInImage.rejectedSourceUrl)"
                            >
                                <vector-icon icon="eye" :size="18" :fill="'#7A7A7A'" />
                            </div>
                        </div>
                    </div>
                    <span class="images__icon">
                        <vector-icon icon="rejected" :size="14" :fill="'#DA5350'" />
                        <span class="images__label rejected"> Rejected </span>
                    </span>
                </div>
            </div>
            <div class="optInSection__item upload-box">
                <div class="optInSection__item__label">
                    <div class="text">Upload Opt-in Image</div>
                    <div class="tooltip">
                        <fa-icon
                            v-tooltip="
                                'Please provide image of the opt-in process and what a subscriber is agreeing to. This should be where the customer’s phone number was entered by the customer agreeing to receive the SMS. Supported files: png, jpg - up to 5MB'
                            "
                            icon="info-circle"
                            class="tooltip-icon"
                        />
                    </div>
                </div>
                <div class="optInSection__item__description">
                    Before uploading the Image, please make sure it meets the below criteria
                </div>
                <form-checkbox
                    v-if="
                        isViaTextSelected ||
                            isOnlineFormSelected ||
                            isQRCodeSelected ||
                            isPaperFormSelected
                    "
                    v-model="policyOptions.option1"
                    @input="handleOption"
                >
                    <template v-if="isViaTextSelected">
                        I have added the Compliance Language to my keyword
                    </template>
                    <template
                        v-if="isViaTextSelected || isPaperFormSelected || isOnlineFormSelected"
                    >
                        I have added the Compliance Language to my form
                    </template>
                    <template v-if="isQRCodeSelected">
                        I have added the Compliance Language to my QR code
                    </template>
                </form-checkbox>
                <form-checkbox
                    v-if="isViaTextSelected || isOnlineFormSelected"
                    v-model="policyOptions.option2"
                    @input="handleOption"
                >
                    I have included a link to my Terms of Service
                </form-checkbox>
                <form-checkbox
                    v-if="isViaTextSelected || isOnlineFormSelected"
                    v-model="policyOptions.option3"
                    @input="handleOption"
                >
                    I have included a link to my Privacy Policy
                </form-checkbox>
                <form-checkbox
                    v-if="isOnlineFormSelected"
                    v-model="policyOptions.option4"
                    @input="handleOption"
                >
                    My website footer contains a link to my Privacy Policy and Terms of Service
                </form-checkbox>
                <p
                    v-if="showPolicyOptionsError"
                    class="help is-danger"
                    v-text="'Please read and check the boxes above'"
                />
            </div>
        </div>

        <div class="complianceModal__column">
            <div
                class="optInSection__item"
                :class="{
                    loadingState: validateLoading,
                }"
            >
                <form-upload
                    label="Opt-in Image"
                    :hideLabelColon="true"
                    :hideRequired="true"
                    :showNewDesign="true"
                    :tooltip="{
                        content:
                            'Please provide image of the opt-in process and what a subscriber is agreeing to. This should be where the customer’s phone number was entered by the customer agreeing to receive the SMS. Supported files: png, jpg - up to 5MB',
                    }"
                    :hideUploader="!!(isAI && files.length && !errors.length)"
                    @on-change="handleAddFiles"
                />

                <div v-if="errors.length" class="optInFilesError">
                    <div v-for="(group, idx) in errors" :key="idx" class="optInFilesError__group">
                        <div
                            v-for="(error, ids) in group"
                            :key="ids"
                            class="optInFilesError__item"
                            v-html="error"
                        />
                    </div>
                </div>

                <a class="optInSection__example" :href="IMAGE_EXAMPLE_LINK" target="_blank">
                    {{ isAI ? 'Examples' : 'See example' }}
                </a>
            </div>

            <div v-if="files.length" class="optInFiles">
                <template v-if="!isAI">
                    <div v-for="(item, idx) in files" :key="idx" class="optInFiles__item">
                        <div class="optInFiles__box">
                            <div v-if="item.image" class="optInFiles__image">
                                <img :src="item.image" alt="" />
                            </div>
                        </div>
                        <div v-if="item.loading" class="optInFiles__box">
                            <div class="optInFiles__loader">
                                <circle-loader :size="30" aria-label="OptIn" />
                            </div>
                        </div>
                        <div
                            v-if="!item.loading"
                            class="optInFiles__box optInFiles__delete"
                            @click="handleDeleteOptInFile(item)"
                        >
                            <fa-icon :icon="['fas', 'trash']" />
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="optInFiles__container">
                        <div v-for="(item, idx) in files" :key="idx" class="optInFiles__item">
                            <template v-if="!isLoading">
                                <div class="optInFiles__box">
                                    <div v-if="item.image" class="optInFiles__image">
                                        <img :src="item.image" alt="" />
                                    </div>
                                </div>
                                <div class="optInFiles__image-hover">
                                    <div class="icon-block" @click="handleDeleteOptInFile(item)">
                                        <fa-icon :icon="['fas', 'trash']" />
                                    </div>
                                    <div class="icon-block" @click="handleShowPreview(item.image)">
                                        <vector-icon icon="eye" :size="18" :fill="'#7A7A7A'" />
                                    </div>
                                </div>
                            </template>
                            <div v-if="item.loading" class="optInFiles__box">
                                <div class="optInFiles__loader">
                                    <circle-loader :size="48" aria-label="OptIn" />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <p
                v-if="showFilesError"
                class="help is-danger"
                v-text="'Please provide image of the opt-in process'"
            />
        </div>

        <div v-if="!isAI" class="complianceModal__column">
            <div class="optInSection__item">
                <form-textarea
                    v-model="description"
                    :hideLabelColon="true"
                    label="Sample message"
                    :tooltip="{
                        content:
                            'Provide an example of a message that you will be sending with this campaign use case. This sample will be used by carriers to help identify your traffic.',
                    }"
                    :error="sampleError(`opt_in_data.${method}.description`)"
                    placeholder="What is the most common message you send to your contacts?"
                />
            </div>
        </div>
        <preview-modal
            v-if="showPreviewModal.isShow"
            :sourceUrl="showPreviewModal.url"
            @close="handleClosePreview"
        />
    </div>
</template>

<script>
import { get } from 'lodash';
import PreviewModal from 'views/Settings/Organization/Settings/Compliance/TollFreeVerificationModal/Steps/OptIn/PreviewModal.vue';

const EXAMPLE_LINK = 'https://help.salesmessage.com/en/articles/8287513-sms-opt-in-guides';
const IMAGE_EXAMPLE_LINK = 'https://help.salesmessage.com/en/articles/8287513-opt-in-guides';

export default {
    components: { PreviewModal },

    props: {
        form: { type: Object, default: null },
        opt_in_data_server: { type: Array, default: () => [] },
        opt_in_data_error: { type: Object, default: null },
        currentTollFreeProfileId: { type: Number, default: 0 },
        isAI: { type: Boolean, default: false },
        showPolicyOptionsError: { type: Boolean, default: false },
        showFilesError: { type: Boolean, default: false },
    },

    emits: ['handleUpdateState', 'onDeleteFile', 'onChange'],

    data: () => ({
        method: 9,
        optionsOptInMethods: [
            {
                id: 9,
                label: 'via Text',
            },
            {
                id: 10,
                label: 'Online form',
            },
            {
                id: 7,
                label: 'Paper Form',
            },
            {
                id: 8,
                label: 'QR Code',
            },
        ],
        description: '',
        relatable_service_type: 2,
        opt_in_data: {},
        filesMap: {},
        EXAMPLE_LINK: EXAMPLE_LINK,
        IMAGE_EXAMPLE_LINK: IMAGE_EXAMPLE_LINK,
        errors: [],
        validateLoading: false,
        initialized: false,
        isLoading: false,
        policyOptions: {
            option1: false,
            option2: false,
            option3: false,
            option4: false,
            showErrorMessage: false,
        },
        showPreviewModal: {
            isShow: false,
            url: '',
        },
        optInImages: [
            {
                id: 9,
                rejectedSourceUrl: `${window.Config.cdn_url}/images/via-text-rejected.png`,
                approvedSourceUrl: `${window.Config.cdn_url}/images/via-text-approved.png`,
            },
            {
                id: 10,
                rejectedSourceUrl: `${window.Config.cdn_url}/images/online-form-rejected.png`,
                approvedSourceUrl: `${window.Config.cdn_url}/images/online-form-approved.png`,
            },
            {
                id: 7,
                rejectedSourceUrl: `${window.Config.cdn_url}/images/paper-form-rejected.png`,
                approvedSourceUrl: `${window.Config.cdn_url}/images/paper-form-approved.png`,
            },
            {
                id: 8,
                rejectedSourceUrl: `${window.Config.cdn_url}/images/qr-code-rejected.png`,
                approvedSourceUrl: `${window.Config.cdn_url}/images/qr-code-approved.png`,
            },
        ],
    }),

    computed: {
        dropdownSettings() {
            return {
                valueField: 'id',
                options: this.optionsOptInMethods,
                render: {
                    item: this.dropdownOptInMethodItem,
                    option: this.dropdownOptInMethodOption,
                },
                maxItems: 1,
                dropdownParent: 'body',
            };
        },

        files() {
            return Object.values(this.filesMap);
        },

        optInDataServerItem() {
            return this.opt_in_data_server.length ? this.opt_in_data_server[0] : null;
        },

        optInLabel() {
            return this.isAI ? 'Opt-in type' : 'Opt-in Method';
        },

        currentOptInImage() {
            return this.optInImages.find((item) => item.id === this.method);
        },

        isViaTextSelected() {
            return this.method === 9;
        },

        isOnlineFormSelected() {
            return this.method === 10;
        },

        isPaperFormSelected() {
            return this.method === 7;
        },

        isQRCodeSelected() {
            return this.method === 8;
        },
    },

    watch: {
        description(value) {
            if (!this.opt_in_data[this.method]) this.opt_in_data[this.method] = {};

            this.opt_in_data[this.method].description = value;

            this.$emit('onChange', this.opt_in_data, this.files, this.handleCheckOptionsState());
        },

        method(newValue, oldValue) {
            delete this.opt_in_data[oldValue];
            this.resetSelectedOptions();

            if (!this.opt_in_data[newValue]) this.opt_in_data[newValue] = {};

            this.opt_in_data[newValue].relatable_service_type = this.relatable_service_type;
            this.opt_in_data[newValue].opt_in_method_id = newValue;
            this.opt_in_data[newValue].description = this.description;

            if (this.initialized) {
                this.resetFiles();
            }

            this.initFiles();
            this.$emit('onChange', this.opt_in_data, this.files, this.handleCheckOptionsState());
        },

        'files.length'() {
            this.$emit('onChange', this.opt_in_data, this.files, this.handleCheckOptionsState());
        },
    },

    methods: {
        async init() {
            this.initData();
            this.initFiles();
            this.$emit('onChange', this.opt_in_data, this.files, this.handleCheckOptionsState());
            await this.initInitialized();
        },

        reset() {
            this.errors = [];
            this.initialized = false;
        },

        resetFiles() {
            this.filesMap = {};
        },

        async initInitialized() {
            await this.$delay(500);
            this.initialized = true;
        },

        initData() {
            if (this.optInDataServerItem) {
                this.method = this.optInDataServerItem.opt_in_method_id;
                this.description = this.optInDataServerItem.description;

                this.opt_in_data[this.method] = {
                    relatable_service_type: this.relatable_service_type,
                    opt_in_method_id: this.method,
                    description: this.description,
                };
            } else {
                this.opt_in_data[this.method] = {
                    relatable_service_type: this.relatable_service_type,
                    opt_in_method_id: this.method,
                    description: this.description,
                };
            }
        },

        initFiles() {
            if (this.optInDataServerItem) {
                const files =
                    this.optInDataServerItem?.opt_in_method_id === this.method
                        ? this.optInDataServerItem.files
                        : [];
                this.filesMap = {};

                files.forEach((file) => {
                    this.filesMap[file.id] = {
                        id: file.id,
                        uuid: file.id,
                        loading: false,
                        file: null,
                        image: file.source_url,
                        is_local: false,
                        opt_in_method_id: this.method,
                        relatable_service_type: this.relatable_service_type,
                        error: '',
                    };
                });
            }
        },

        async handleValidateFile(file) {
            try {
                this.validateLoading = true;
                this.$emit('handleUpdateState', ['validateLoadingFileOptIn', true]);
                let formData = new FormData();
                formData.append('file', file, file.name);

                await this.$store.dispatch('updateOptInDataFilesValidate', formData);

                return true;
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleAddFiles');

                let error = e?.response?.data?.file;

                if (Array.isArray(error) && error.length) {
                    this.errors.push(error);
                }

                return false;
            } finally {
                this.validateLoading = false;
                this.$emit('handleUpdateState', ['validateLoadingFileOptIn', false]);
            }
        },

        handleAddFiles(files) {
            this.errors = [];
            this.isLoading = true;

            files.forEach(async (file) => {
                const status = await this.handleValidateFile(file.file);

                if (!status) return;

                this.filesMap[file.uuid] = {
                    id: file.uuid,
                    loading: false,
                    file: file.file,
                    image: '',
                    is_local: true,
                    opt_in_method_id: this.method,
                    relatable_service_type: this.relatable_service_type,
                    error: '',
                };

                if (FileReader) {
                    const fr = new FileReader();
                    fr.onload = () => {
                        this.filesMap[file.uuid].image = fr.result;
                    };
                    fr.readAsDataURL(file.file);
                }
            });

            this.isLoading = false;
        },

        handleDeleteOptInFile(item) {
            this.isLoading = true;
            this.$emit('onDeleteFile', {
                file: item,
                handleDelete: () => {
                    const id = Object.entries(this.filesMap)
                        .filter(([key, value]) => value.id === item.id)
                        .reduce((state, [key]) => key, null);

                    if (id) {
                        delete this.filesMap[id];
                    }
                },
            });
            this.isLoading = false;
        },

        handleChangeOptInMethods(value) {
            this.method = Number(value);
        },

        sampleError(key) {
            const error = get(this.opt_in_data_error, key);

            return Array.isArray(error) ? error[0] : '';
        },

        handleShowPreview(sourceUrl) {
            this.showPreviewModal = {
                isShow: true,
                url: sourceUrl,
            };
        },

        handleClosePreview() {
            this.showPreviewModal = {
                isShow: false,
                url: '',
            };
        },

        handleOption() {
            this.$emit('onChange', this.opt_in_data, this.files, this.handleCheckOptionsState());
        },

        handleCheckOptionsState() {
            if (
                (this.isViaTextSelected ||
                    (this.isOnlineFormSelected && this.policyOptions.option4)) &&
                this.policyOptions.option1 &&
                this.policyOptions.option2 &&
                this.policyOptions.option3
            ) {
                return true;
            }

            return (
                (this.isPaperFormSelected || this.isQRCodeSelected) && this.policyOptions.option1
            );
        },

        resetSelectedOptions() {
            this.policyOptions = {
                option1: false,
                option2: false,
                option3: false,
                option4: false,
                showErrorMessage: false,
            };
        },

        dropdownOptInMethodItem(item, escape) {
            return `<div class='selectize-contact'>
                        <span>${escape(item.label)}</span>
                    </div>`;
        },

        dropdownOptInMethodOption(item, escape) {
            return `<div class='selectize-option__columns'>
                        <div class='selectize-option__column'>
                            <span>
                                ${escape(item.label)}
                            </span>
                        </div>
                    </div>`;
        },
    },

    mounted() {
        this.init();
    },

    beforeUnmount() {
        this.reset();
    },
};
</script>

<style lang="scss" scoped>
@import './styles.module.scss';
</style>
